import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'Our Brands', href: '/brands' },
    { name: 'Services', href: '/services' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-gray-950/98 backdrop-blur-md border-b border-amber-500/20 shadow-lg shadow-black/40'
          : 'bg-gray-950 border-b border-amber-500/10'
      }`}
    >
      <nav className="px-4 sm:px-6 lg:px-10">
        <div className="flex justify-between items-center h-18 py-3">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <div className="relative">
                <img
                  src="https://static.readdy.ai/image/1aff587802347e8a3b9191c90eecded3/c67bf49259dce1e180324b942fef6f74.png"
                  alt="Villarreal Enterprise Group Logo"
                  className="h-10 w-auto"
                />
              </div>
              <div className="ml-3">
                <div className="text-base font-bold text-white whitespace-nowrap leading-tight group-hover:text-amber-400 transition-colors duration-300">
                  Villarreal Enterprise Group
                </div>
                <div className="text-xs text-amber-500/70 tracking-widest uppercase whitespace-nowrap">
                  Las Vegas, Nevada
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`relative px-4 py-2 text-sm font-medium rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap group ${
                  isActive(item.href)
                    ? 'text-amber-400'
                    : 'text-gray-300 hover:text-amber-400'
                }`}
              >
                {item.name}
                <span
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-amber-500 transition-all duration-300 rounded-full ${
                    isActive(item.href) ? 'w-4/5' : 'w-0 group-hover:w-4/5'
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/contact">
              <button className="whitespace-nowrap cursor-pointer text-sm text-amber-400 border border-amber-500/40 px-4 py-2 rounded-md hover:bg-amber-500/10 transition-all duration-300 font-medium">
                <i className="ri-phone-line mr-1.5"></i>
                Contact
              </button>
            </Link>
            <Link to="/services">
              <Button variant="primary" size="sm">
                Get Started
                <i className="ri-arrow-right-line ml-1.5"></i>
              </Button>
            </Link>
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="w-10 h-10 flex items-center justify-center text-amber-400 hover:text-amber-300 cursor-pointer border border-amber-500/30 rounded-md hover:bg-amber-500/10 transition-all duration-300"
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-amber-500/20 bg-gray-950">
            <div className="px-2 pt-3 pb-4 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-3 text-base font-medium rounded-lg cursor-pointer transition-all duration-200 ${
                    isActive(item.href)
                      ? 'text-amber-400 bg-amber-500/10 border-l-2 border-amber-500'
                      : 'text-gray-300 hover:text-amber-400 hover:bg-amber-500/5'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="px-2 pt-3 pb-1">
                <Link to="/services" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="primary" size="md" className="w-full">
                    Get Started
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
